// This file is responsible for drawing routes.
//  It does not draw the pins for the stops; only the lines between them.
//
// Ambassador: Yatharth.
//
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { getColorFromPalette } from './colors';

const eraseRenderer = (renderer: google.maps.DirectionsRenderer) => renderer.setDirections(null);

// Use this function for each polyline you want to draw. When you call `.setDirections()`,
// on a directionsRenderer for the second time, it will override the polyline plotted
//  by the previous call. You can pass in `emptyResult` to erase the directions entirely.
const makeRenderer = ({
    map,
    maps,
    routeColor,
}: {
    map: google.maps.Map;
    maps: typeof google.maps;
    routeColor: string;
}): google.maps.DirectionsRenderer => {
    const renderer = new maps.DirectionsRenderer({
        polylineOptions: {
            strokeColor: routeColor,
            strokeOpacity: 0.9,
            strokeWeight: 7,
        },
    });
    renderer.setOptions({
        suppressMarkers: true, // By default, Google shows their own markers. Suppress those.
        preserveViewport: true, // Prevent re-centering after calculating directions.
    });
    renderer.setMap(map);
    return renderer;
};

export const useDrawRoutes = ({
    map,
    maps,
    directionResults: results,
    drawRoutes,
}: {
    map: google.maps.Map | null;
    maps: typeof google.maps | null;
    directionResults: (google.maps.DirectionsResult | null)[];
    drawRoutes: boolean;
}) => {
    //

    // Direction Renderers are the things that draw the lines on the map.
    //  We need to have at least N of them in stock to draw N lines.
    const [renderers, setRenderers] = useState<google.maps.DirectionsRenderer[]>([]);

    useEffect(() => {
        //

        // If `map` or `maps` is null, then Google Maps is not ready yet.
        //  No worries. This hook will get called again with the new `map`
        //  and `maps` parameters when they get set, and this effect will rerun.
        if (!map || !maps) return;

        // If we're not drawing routes, erase everything!
        if (!drawRoutes) {
            renderers.forEach(eraseRenderer);
            return;
        }

        // If we don't have enough direction renderers, we need to make some more.
        //  then let's make some more.
        if (renderers.length < results.length) {
            // We make sure to iterate over [renderers.length, results.length),
            //  and not [0, results.length - renderers.length) -- even though
            //  those ranges have the same length.
            // This is because we base the color of the polyline based on the group
            //  index. We have to account for the fact that the first `renderers.length`
            //  colors have already been used up.
            const newRenderers = _.range(renderers.length, results.length).map((groupIndex) =>
                makeRenderer({
                    map,
                    maps,
                    routeColor: getColorFromPalette(groupIndex),
                }),
            );

            setRenderers((prevRenderers) => [...prevRenderers, ...newRenderers]);

            // We do an early return. When `setRenderers()` finishes mutating the state,
            //  this effect will run again, because `renderers` will change, which this
            //  effect depends on.
            return;
        }

        // Now let's draw!
        results.forEach((result, groupIndex) => {
            renderers[groupIndex].setDirections(result);
        });

        // Don't forget to erase any renderers not in use, or we'll see phantom directions on the map.
        renderers.slice(results.length).forEach(eraseRenderer);

        //
    }, [map, maps, results, drawRoutes, renderers]);
};
